type UnknownRecord = Record<string, unknown>;

export interface IncorporatedPricingSemanticRow {
    readonly activityDescription?: unknown;
    readonly lineDescription?: unknown;
}

const LINEAGE_STOP_WORDS = new Set([
    'all', 'and', 'appointed', 'apply', 'builder', 'client', 'complete', 'construct',
    'contractor', 'coordination', 'engaged', 'excluded', 'exclusion', 'existing', 'for',
    'from', 'high', 'in', 'including', 'install', 'minimum', 'new', 'nominated', 'of',
    'on', 'other', 'others', 'outside', 'owner', 'provide', 'repair', 'required', 'scope',
    'separate', 'specified', 'strata', 'supply', 'system', 'the', 'to', 'with', 'work',
]);

const GENERIC_PRICING_WORDS = new Set([
    'allowance', 'amount', 'cost', 'crew', 'day', 'each', 'equipment', 'hour', 'item',
    'labour', 'labor', 'lot', 'material', 'rate', 'subcontract', 'subcontractor', 'sum',
    'total',
]);

const GENERIC_SUBJECT_WORDS = new Set([
    'balustrade', 'concrete', 'door', 'drain', 'floor', 'glass', 'hob', 'membrane',
    'paint', 'paver', 'roof', 'screen', 'slab', 'steel', 'tile', 'timber', 'wall',
]);

const CONSTRUCTIVE_ACTION = /\b(?:apply|build|construct(?:ed|ing|ion)?|create|erect|fabricat(?:e|ed|ing|ion)|fit(?:ted|ting)?|form|install(?:ed|ing|ation)?|lay|new|place|provide|reinstate|renew|repair|replace|seal|supply|waterproof)\b/i;
const REMOVAL_ACTION = /\b(?:break\s*out|demolish(?:ed|ing|ion)?|discard|dismantl(?:e|ed|ing)|dispos(?:e|ed|ing|al)|remove(?:d|s|ing|al)?|strip(?:ped|ping)?|take\s+down)\b/i;
const CONTEXT_BOUNDARY = /\b(?:at|around|against|behind|below|above|beneath|along|adjacent\s+to|interface(?:s)?\s+(?:at|to|with)|junction(?:s)?\s+(?:at|to|with)|abut(?:s|ted|ting|ment)?(?:\s+to|\s+with)?)\b/i;
const CLIENT_SOURCE_BOUNDARY = /\b(?:by\s+others|excluded|exclusion|not\s+included|outside\s+(?:the\s+)?(?:builder(?:'s)?|contract(?:or)?(?:'s)?)?\s*scope|coordination\s+only|separate\s+(?:contractor|trade|package)|(?:client|owner|strata)\s+(?:appointed|engaged|supplied))\b/i;
const DIRECT_PACKAGE_SALE_ACTION = /\b(?:build|construct(?:ed|ing|ion)?|erect|fabricat(?:e|ed|ing|ion)|fit(?:ted|ting)?|install(?:ed|ing|ation)?|place|provide|reinstate|renew|replace|supply)\b/i;
const BOUNDARY_ADJACENT_HEAD_CONTEXT = new Set([
    'access', 'barrier', 'guard', 'interface', 'junction', 'membrane', 'protection',
    'protective', 'temporary', 'termination', 'upturn', 'waterproof', 'waterproofing',
]);
const SOURCE_BOUNDARY_MARKER = /\b(?:by\s+(?:others|owner|client|principal|separate\s+contractor)|not\s+by\s+(?:the\s+)?builder|excluded\s+from\s+(?:the\s+)?builder(?:'s)?\s+scope|n\.?i\.?c\.?)\b/i;
const PARENTHETICAL_SOURCE_BOUNDARY = /\(\s*(?:to\s+be\s+)?(?:attached|built|constructed|installed|provided|replaced|supplied)\s+by\s+(?:others|owner|client|principal|separate\s+contractor)\b/i;
const BOUNDARY_SUBJECT_DESIGNATOR = /\b(?:new|existing|replacement|temporary|proposed)\b/gi;
const BOUNDARY_ORPHAN_WORDS = new Set([
    'and', 'are', 'attached', 'be', 'been', 'being', 'built', 'constructed', 'installed',
    'is', 'item', 'package', 'provided', 'replaced', 'scope', 'supplied', 'the', 'to',
    'was', 'were', 'will', 'work', 'works',
]);

function normaliseToken(token: string): string {
    if (token === 'balstrade' || token === 'balstrades') return 'balustrade';
    if (token.endsWith('ies') && token.length > 5) return `${token.slice(0, -3)}y`;
    if (token.endsWith('s') && token.length > 4 && !token.endsWith('ss')) return token.slice(0, -1);
    return token;
}

function lineageTokens(value: unknown): Set<string> {
    return new Set((String(value || '').toLowerCase().match(/[a-z][a-z0-9-]{2,}/g) || [])
        .map(normaliseToken)
        .filter(token => !LINEAGE_STOP_WORDS.has(token)));
}

function primaryPackageTokens(value: unknown): Set<string> {
    const source = String(value || '');
    const match = CONTEXT_BOUNDARY.exec(source);
    if (!match || match.index <= 0) return lineageTokens(source);
    const primary = lineageTokens(source.slice(0, match.index));
    return primary.size > 0 ? primary : lineageTokens(source);
}

function terminalCompoundPackageTokens(value: unknown): Set<string> | null {
    const subject = sourceBoundarySubjectForLineage(value);
    // Commas/semicolons make a deliberate multi-package list explicit; every
    // listed package remains protected. Tail anchoring is only for ambiguous,
    // delimiter-less OCR concatenation.
    if (/[,;]/.test(subject)) return null;
    const compound = subject.match(/\b([a-z0-9/-]+\s+and\s+[a-z0-9/-]+(?:\s+[a-z0-9/-]+)?)\s*$/i);
    if (!compound) return null;
    const tokens = lineageTokens(compound[1]);
    return tokens.size > 0 ? tokens : null;
}

function projectsTerminalCompoundPackage(requirement: unknown, projected: unknown): boolean {
    const terminal = terminalCompoundPackageTokens(requirement);
    return !terminal || sharedTokens(terminal, primaryPackageTokens(projected)).length > 0;
}

function projectsExplicitListedPackage(requirement: unknown, projected: unknown): boolean {
    const subject = sourceBoundarySubjectForLineage(requirement);
    if (!/[,;]/.test(subject)) return false;
    const required = primaryPackageTokens(subject);
    const candidate = primaryPackageTokens(projected);
    return candidate.size > 0 && [...candidate].every(token => required.has(token));
}

function actionFamilies(value: unknown): Readonly<{ constructive: boolean; removal: boolean }> {
    const source = String(value || '');
    return {
        constructive: CONSTRUCTIVE_ACTION.test(source),
        removal: REMOVAL_ACTION.test(source),
    };
}

function actionsAreCompatible(requirement: unknown, pricing: unknown): boolean {
    const required = actionFamilies(requirement);
    const priced = actionFamilies(pricing);
    if (required.constructive && !required.removal && priced.removal && !priced.constructive) return false;
    if (required.removal && !required.constructive && priced.constructive && !priced.removal) return false;
    return true;
}

function sharedTokens(left: Set<string>, right: Set<string>): string[] {
    return [...left].filter(token => right.has(token));
}

function hasStrongSubjectMatch(requirement: unknown, pricing: unknown): boolean {
    const required = primaryPackageTokens(requirement);
    const priced = primaryPackageTokens(pricing);
    const shared = sharedTokens(required, priced);
    if (shared.length >= 2) return true;
    if (shared.length !== 1) return false;
    const token = shared[0];
    if (!token) return false;
    if (required.size === 1 || priced.size === 1) return true;
    return token.length >= 7 && !GENERIC_SUBJECT_WORDS.has(token);
}

function hasStrongClientSubjectMatch(requirement: unknown, projected: unknown): boolean {
    const required = primaryPackageTokens(requirement);
    const client = primaryPackageTokens(projected);
    const shared = sharedTokens(required, client);
    if (shared.length >= 2) return true;
    if (shared.length !== 1) return false;
    const token = shared[0];
    if (!token) return false;
    // A package-agnostic boundary may legitimately be a single short head
    // (Window, Door). Exact one-token identity is sufficient here because the
    // caller separately requires an explicit client boundary cue; generic
    // "Work by others" still has no shared package token.
    if (required.size === 1 && client.size === 1) return true;
    return token.length >= 7 && !GENERIC_SUBJECT_WORDS.has(token);
}

function hasDirectConstructivePackageHeadMatch(requirement: unknown, projected: unknown): boolean {
    const required = primaryPackageTokens(requirement);
    const client = primaryPackageTokens(projected);
    const shared = sharedTokens(required, client);
    if (shared.length !== 1 || !GENERIC_SUBJECT_WORDS.has(shared[0]!)) return false;
    // A shortened direct sale must still name the package head, not merely a
    // generic material that appears earlier in a more specific package. For
    // example, "repair concrete voids" must not sell a source-owned "400mm
    // concrete hob", while "install replacement balustrade" may safely match
    // the head of "glass balustrade".
    const genericPackageHead = [...required]
        .filter(token => GENERIC_SUBJECT_WORDS.has(token))
        .at(-1);
    if (!genericPackageHead || shared[0] !== genericPackageHead) return false;
    const clientContext = [...client].filter(token => token !== shared[0]);
    return DIRECT_PACKAGE_SALE_ACTION.test(String(projected || ''))
        && (clientContext.length === 0
            || !clientContext.every(token => BOUNDARY_ADJACENT_HEAD_CONTEXT.has(token)));
}

function hasSpecificPricingSubject(value: unknown): boolean {
    const tokens = primaryPackageTokens(value);
    return [...tokens].some(token => !GENERIC_PRICING_WORDS.has(token));
}

function semanticTextForPricingRow(row: IncorporatedPricingSemanticRow | UnknownRecord): string {
    const lineDescription = String(row.lineDescription || '').trim();
    if (hasSpecificPricingSubject(lineDescription)) return lineDescription;
    return String(row.activityDescription || lineDescription).trim();
}

/**
 * Isolate the package that an OCR/layout fragment actually assigns to others.
 * PDF column extraction can concatenate an in-scope membrane label with a
 * later "privacy screen by others" label. Treating the whole concatenation as
 * the excluded package makes legitimate membrane Pricing look like leakage.
 *
 * This deliberately does not use a package vocabulary: flashings, scaffold,
 * air-conditioning and future trades must be protected without a release.
 * Direct boundaries prefer the nearest punctuation/layout clause and retain
 * its bounded trailing qualifiers (for example "posts to be supplied and
 * installed"). A parenthetical "(to be attached by others)" modifies the
 * preceding clause, so its complete prefix remains the subject. An orphaned
 * continuation such as "be attached by others Fall" has no package subject
 * and is returned unresolved. It remains an immutable audit warning with zero
 * activity, Pricing or sale authority; no client wording is invented.
 */
export function sourceBoundarySubjectForLineage(value: unknown): string {
    // Preserve line breaks until the nearest layout clause is isolated; a
    // premature `\s+` collapse recreates the mixed-column false positive this
    // parser exists to prevent.
    const source = String(value || '')
        .replace(/\r\n?/g, '\n')
        .replace(/[\t\f\v ]+/g, ' ')
        .trim();
    if (!source) return '';
    const hasNominalSubject = (candidate: string): boolean => {
        const words = candidate.toLowerCase().match(/[a-z][a-z0-9/-]*/g) || [];
        return words.some(word => !BOUNDARY_ORPHAN_WORDS.has(word));
    };
    const parenthetical = PARENTHETICAL_SOURCE_BOUNDARY.exec(source);
    if (parenthetical && parenthetical.index > 0) {
        const subject = source.slice(0, parenthetical.index).replace(/\s+/g, ' ').trim();
        return hasNominalSubject(subject) ? subject : '';
    }
    const marker = SOURCE_BOUNDARY_MARKER.exec(source);
    if (!marker) return source;
    let prefix = source.slice(0, marker.index).trim();
    if (!prefix) return '';

    // Real PDF extraction often leaves independent table cells on one line.
    // A punctuation/layout separator is a stronger boundary than casing.
    const explicitSegments = prefix.split(/(?:\n+|[;|•]|\.{2,})\s*/).map(segment => segment.trim()).filter(Boolean);
    if (explicitSegments.length > 1) prefix = explicitSegments.at(-1)!;

    // A later designator is a common table-cell start: "... Base New privacy
    // screen by others". Keep a designator at position zero as part of the
    // ordinary clause.
    const designators = [...prefix.matchAll(BOUNDARY_SUBJECT_DESIGNATOR)];
    const lastDesignator = designators.at(-1);
    if (lastDesignator?.index != null && lastDesignator.index > 0) {
        prefix = prefix.slice(lastDesignator.index).trim();
    } else {
        // Casing transitions preserve package independence without knowing a
        // package noun. Handles "... waterproof membrane Paver and pedestal
        // system by others" and title-cased OCR cells such as "Privacy screen".
        const words = [...prefix.matchAll(/[A-Za-z0-9][A-Za-z0-9/-]*/g)];
        let clauseStart = -1;
        for (let index = 1; index < words.length; index += 1) {
            const current = words[index]?.[0] || '';
            const previous = words[index - 1]?.[0] || '';
            const next = words[index + 1]?.[0] || '';
            const startsUpper = /^[A-Z]/.test(current);
            const lowerToUpper = /^[a-z]/.test(previous) && startsUpper;
            const titledCell = index >= 2 && startsUpper && /^[a-z]/.test(next);
            if (lowerToUpper || titledCell) clauseStart = words[index]!.index!;
        }
        if (clauseStart > 0) prefix = prefix.slice(clauseStart).trim();
    }
    // Remove the source-boundary predicate so short package subjects remain
    // exact: "Scaffolding is by others" -> "Scaffolding"; "Window to be
    // supplied and installed by others" -> "Window". This also prevents the
    // generic action words from masquerading as the package in an orphan.
    prefix = prefix.replace(
        /\s+(?:(?:(?:is|are|was|were)(?:\s+to\s+be)?|(?:(?:to|will|shall)\s+be))(?:\s+(?:attached|built|constructed|disconnected|erected|fabricated|installed|provided|reinstated|replaced|supplied)(?:\s+and\s+(?:attached|built|constructed|disconnected|erected|fabricated|installed|provided|reinstated|replaced|supplied))*)?|(?:attached|built|constructed|disconnected|erected|fabricated|installed|provided|reinstated|replaced|supplied)(?:\s+and\s+(?:attached|built|constructed|disconnected|erected|fabricated|installed|provided|reinstated|replaced|supplied))*|works?)\s*$/i,
        '',
    ).replace(/\s+/g, ' ').trim();
    return hasNominalSubject(prefix) ? prefix : '';
}

/**
 * Proves that a cited Pricing row describes the same package and a compatible
 * delivery action as the incorporated technical requirement. Context nouns
 * after boundaries such as "at ... interface" cannot stand in for the package
 * being sold, and demolition/removal cannot satisfy construction/installation.
 */
export function pricingLineSupportsIncorporatedRequirement(
    requirement: unknown,
    row: IncorporatedPricingSemanticRow,
): boolean {
    const pricing = semanticTextForPricingRow(row);
    return Boolean(pricing)
        && actionsAreCompatible(requirement, pricing)
        && hasStrongSubjectMatch(requirement, pricing);
}

/**
 * Detects a separately priced source-boundary package. A contextual mention
 * (for example, membrane upturns at a glass-balustrade interface) is not the
 * primary package, while "glass balustrade supply and installation" is.
 */
export function pricingLinePricesExcludedRequirement(
    requirement: unknown,
    row: IncorporatedPricingSemanticRow,
): boolean {
    const pricing = semanticTextForPricingRow(row);
    if (!pricing || !actionsAreCompatible(requirement, pricing)) return false;
    const boundarySubject = sourceBoundarySubjectForLineage(requirement);
    if (!boundarySubject) return false;
    const required = primaryPackageTokens(boundarySubject);
    const priced = primaryPackageTokens(pricing);
    const shared = sharedTokens(required, priced);
    return projectsTerminalCompoundPackage(boundarySubject, pricing) && (
        (required.size === 1 && shared.length === 1)
        || shared.length >= 2
        || shared.some(token => token.length >= 5)
    );
}

/**
 * Proves that one current client-facing scope fragment carries an incorporated
 * builder requirement. Callers deliberately compare individual title,
 * description and inclusion fragments: concatenating a whole quote lets an
 * unrelated nearby noun create false coverage across package boundaries.
 */
export function clientScopeTextProjectsIncorporatedRequirement(
    requirement: unknown,
    clientText: unknown,
): boolean {
    const projected = String(clientText || '').trim();
    return Boolean(projected)
        && actionsAreCompatible(requirement, projected)
        && hasStrongClientSubjectMatch(requirement, projected);
}

/**
 * Detects when client-facing scope positively sells a package that its source
 * assigns to others. Boundary wording and removal-only access work are not a
 * constructive projection; contextual mentions remain constrained by the
 * primary-package matcher rather than whole-fragment word overlap.
 */
export function clientScopeTextConstructivelyProjectsSourceBoundaryRequirement(
    requirement: unknown,
    clientText: unknown,
): boolean {
    const projected = String(clientText || '').trim();
    const boundarySubject = sourceBoundarySubjectForLineage(requirement);
    const actions = actionFamilies(projected);
    return Boolean(projected && boundarySubject)
        && actions.constructive
        && CLIENT_SOURCE_BOUNDARY.test(projected) === false
        && actionsAreCompatible(boundarySubject, projected)
        && projectsTerminalCompoundPackage(boundarySubject, projected)
        // A direct constructive sale may shorten "glass balustrade" to its
        // package head noun ("install replacement balustrade"). Context-only
        // qualifiers such as temporary protection and access remain excluded.
        && (hasStrongSubjectMatch(boundarySubject, projected)
            || hasDirectConstructivePackageHeadMatch(boundarySubject, projected));
}

/**
 * A source-owned package is carried to the client only when the current
 * exclusion/coordination copy both identifies the same specific subject and
 * states a real payer/scope boundary. A contextual noun alone is insufficient.
 */
export function clientBoundaryTextProjectsIncorporatedRequirement(
    requirement: unknown,
    clientText: unknown,
): boolean {
    const projected = String(clientText || '').trim();
    const boundarySubject = sourceBoundarySubjectForLineage(requirement);
    const required = terminalCompoundPackageTokens(boundarySubject) || primaryPackageTokens(boundarySubject);
    const client = primaryPackageTokens(projected);
    return Boolean(projected && boundarySubject && required.size > 0)
        && CLIENT_SOURCE_BOUNDARY.test(projected)
        && actionsAreCompatible(boundarySubject, projected)
        && [...required].every(token => client.has(token));
}

/** Any-package matcher for explicit multi-package review/leak diagnostics. */
export function clientBoundaryTextProjectsAnyIncorporatedRequirementPackage(
    requirement: unknown,
    clientText: unknown,
): boolean {
    const projected = String(clientText || '').trim();
    const boundarySubject = sourceBoundarySubjectForLineage(requirement);
    return Boolean(projected && boundarySubject)
        && CLIENT_SOURCE_BOUNDARY.test(projected)
        && actionsAreCompatible(boundarySubject, projected)
        && projectsTerminalCompoundPackage(boundarySubject, projected)
        && (hasStrongClientSubjectMatch(boundarySubject, projected)
            || projectsExplicitListedPackage(boundarySubject, projected));
}
